/**
 * Daily Challenge Types
 * Defines the structure for daily reading challenges
 */

export type ChallengeId = 
  | 'wordSprint'
  | 'speedRun'
  | 'nightOwl'
  | 'earlyBird'
  | 'volumeKing'
  | 'streakDefender';

export type ChallengeCategory = 'speed' | 'volume' | 'time' | 'streak';

export interface Challenge {
  id: ChallengeId;
  name: string;
  description: string;
  category: ChallengeCategory;
  xpReward: number;
  requirement: number;
  unit: string;
  icon: string;
}

export interface UserChallenge {
  id: string;
  userId: string;
  challengeId: ChallengeId;
  date: string; // ISO date string
  progress: number;
  completed: boolean;
  claimed: boolean;
  claimedAt?: string;
}

export interface DailyChallengeSet {
  date: string;
  challenges: ChallengeId[];
}

/**
 * All available challenges
 */
export const CHALLENGES: Record<ChallengeId, Challenge> = {
  wordSprint: {
    id: 'wordSprint',
    name: 'Word Sprint',
    description: 'Read 500 words in a single session',
    category: 'volume',
    xpReward: 15,
    requirement: 500,
    unit: 'words',
    icon: '⚡',
  },
  speedRun: {
    id: 'speedRun',
    name: 'Speed Run',
    description: 'Achieve 400+ WPM in a session',
    category: 'speed',
    xpReward: 20,
    requirement: 400,
    unit: 'WPM',
    icon: '🚀',
  },
  nightOwl: {
    id: 'nightOwl',
    name: 'Night Owl',
    description: 'Complete a reading session after 10 PM',
    category: 'time',
    xpReward: 10,
    requirement: 1,
    unit: 'session',
    icon: '🦉',
  },
  earlyBird: {
    id: 'earlyBird',
    name: 'Early Bird',
    description: 'Complete a reading session before 7 AM',
    category: 'time',
    xpReward: 10,
    requirement: 1,
    unit: 'session',
    icon: '🐦',
  },
  volumeKing: {
    id: 'volumeKing',
    name: 'Volume King',
    description: 'Read 2,000 words today',
    category: 'volume',
    xpReward: 25,
    requirement: 2000,
    unit: 'words',
    icon: '👑',
  },
  streakDefender: {
    id: 'streakDefender',
    name: 'Streak Defender',
    description: 'Maintain a 7+ day reading streak',
    category: 'streak',
    xpReward: 30,
    requirement: 7,
    unit: 'days',
    icon: '🛡️',
  },
};

/**
 * Challenge categories for filtering
 */
export const CHALLENGE_CATEGORIES: Record<ChallengeCategory, { label: string; color: string }> = {
  speed: { label: 'Speed', color: 'text-orange-500' },
  volume: { label: 'Volume', color: 'text-blue-500' },
  time: { label: 'Time', color: 'text-purple-500' },
  streak: { label: 'Streak', color: 'text-green-500' },
};
